import React from 'react';
import { Select } from 'flowbite-react';
import { customerInformationStrings } from './CustomerInformation.strings';
import { AccountManagerRoster, accountManagerDisplayName } from './accountManagerAssignment';

const S = customerInformationStrings.accountManager;

/** Option value of "Not assigned" (DS clears the manager with `key: null`). */
const NOT_ASSIGNED = '';
/** Option value of a current key that matches no manager; never saved. */
const UNKNOWN_CURRENT = '__unknown__';

interface AccountManagerSelectProps {
  /** The member's current manager key. */
  value: string | null | undefined;
  roster: AccountManagerRoster;
  ariaLabel: string;
  disabled?: boolean;
  /** A manager key, or `null` for "Not assigned". */
  onChange: (key: string | null) => void;
}

/**
 * The compact manager select of a member (list row and detail): "Not
 * assigned" plus every active manager. A current inactive manager stays shown
 * as "<name> (inactive)", and a key that matches no manager as "-", until the
 * operator picks another value; neither can be picked again.
 */
const AccountManagerSelect: React.FC<AccountManagerSelectProps> = ({
  value,
  roster,
  ariaLabel,
  disabled,
  onChange,
}) => {
  const current = value || NOT_ASSIGNED;
  const currentManager = value ? roster.byKey[value] : undefined;
  const currentIsChoice = !value || roster.active.some((m) => m.key === value);
  const selected = value && !currentManager ? UNKNOWN_CURRENT : current;

  return (
    <Select
      sizing="sm"
      aria-label={ariaLabel}
      value={selected}
      disabled={disabled}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => {
        const next = e.target.value;
        if (next === selected || next === UNKNOWN_CURRENT) return;
        onChange(next === NOT_ASSIGNED ? null : next);
      }}
    >
      <option value={NOT_ASSIGNED}>{S.notAssigned}</option>
      {!currentIsChoice && currentManager && (
        <option value={current} disabled>
          {accountManagerDisplayName(currentManager)}
        </option>
      )}
      {!currentIsChoice && !currentManager && (
        <option value={UNKNOWN_CURRENT} disabled>
          {S.unknown}
        </option>
      )}
      {roster.active.map((manager) => (
        <option key={manager.key} value={manager.key}>
          {accountManagerDisplayName(manager)}
        </option>
      ))}
    </Select>
  );
};

export default AccountManagerSelect;
